import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAssignments, useChildren } from '../hooks';
import { queryKeys } from '../hooks/queryKeys';
import { auth, db, functions } from '../services/firebase';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import SignModal from '../components/SignModal';
import { PickupPhotosSection } from '../components/parent/PickupPhotosSection';
import { splitTextByPlaceholders } from '../utils/placeholders';
import type { Assignment, Declaration, Language } from '@shared/types';
import { DEFAULT_LEGAL_BASIS } from '@shared/types';
import type { FieldConfig, TableFieldConfig, TableRowValue } from '@shared/types/interactiveFields';
import Tooltip from '../components/Tooltip';
import { Badge, InlineError, InlineStatus, PageHeader } from '../components/ui';
import { AssignmentsLoadProblem, AssignmentsNoFamily, RefreshProblem } from '../components/parent/LoadProblem';
import { appCheckedFetch } from '../utils/appCheckedFetch';
import { pdfDownloadErrorKey } from '../utils/pdfDownloadError';
import { getErrorMessage } from '@shared/utils/errorUtils';
import { pickNewestSignature } from '@shared/utils/signatureOrder';
import { useDeclarations } from '../hooks/useDeclarations';
import { liveDeclarationTitle } from '@shared/utils/signedDocumentSource';
import { localizedText } from '@shared/utils/localizedString';
import { declarationStatusDisplay } from '@shared/utils/declarationStatus';
import { dateLocaleFor } from '@shared/utils/declarationVersionText';

const CONSENT_BASED = new Set(['Art6(1)(a)', 'Art9(2)(a)']);

/**
 * Replace system placeholders in declaration text with actual values.
 */
function replaceSystemPlaceholders(
  text: string,
  parentName: string,
  childName: string,
  language: Language
): string {
  const currentDate = new Date();
  // Локалът идва от ЕДИНСТВЕНАТА таблица език→локал (`declarationVersionText`),
  // не от закован низ и не от нова таблица тук.
  const formattedDate = currentDate.toLocaleDateString(dateLocaleFor(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const academicYear = month >= 8 ? `${year}/${year + 1}` : `${year - 1}/${year}`;

  return text
    .replace(/\{parent_name\}/g, parentName || '_______________')
    .replace(/\{selected_children_names\}/g, childName || '_______________')
    .replace(/\{current_date\}/g, formattedDate)
    .replace(/\{academic_year\}/g, academicYear);
}

interface ViewModalData {
  assignment: Assignment;
  declarationContent: string;
  fieldsSchema?: FieldConfig[];
  signatureData?: any;
  templateUrl?: string;
}

/**
 * Component to render declaration content with inline tables
 */
function DeclarationContentRenderer({
  content,
  fieldsSchema,
  fieldValues,
}: {
  content: string;
  fieldsSchema?: FieldConfig[];
  fieldValues?: Record<string, unknown>;
}) {
  const { t, i18n } = useTranslation();
  const lang = ((i18n.language || 'bg').split('-')[0]) as Language;

  if (!fieldValues || !fieldsSchema) {
    return <span>{content}</span>;
  }

  const segments = splitTextByPlaceholders(content);
  const fieldsById = fieldsSchema.reduce((acc, field) => {
    acc[field.id] = field;
    return acc;
  }, {} as Record<string, FieldConfig>);

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          return <span key={index}>{segment.content}</span>;
        }

        // Placeholder segment
        const fieldId = segment.fieldId!;
        const field = fieldsById[fieldId];
        const value = fieldValues[fieldId];

        if (!field) {
          return <span key={index} className="text-red-500">[{fieldId}]</span>;
        }

        // Handle table fields
        if (field.type === 'table') {
          const tableField = field as TableFieldConfig;
          const rows = value as TableRowValue[] | undefined;

          if (!rows || rows.length === 0) {
            return <span key={index} className="text-gray-500">{t('common.noData')}</span>;
          }

          return (
            <div key={index} className="my-3">
              <table className="min-w-full border border-gray-300 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    {tableField.columns.map((col) => (
                      <th
                        key={col.id}
                        className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700"
                      >
                        {localizedText(col.header, lang, col.id)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={row.rowId || rowIndex} className={rowIndex % 2 === 1 ? 'bg-gray-50' : ''}>
                      {tableField.columns.map((col) => (
                        <td key={col.id} className="border border-gray-300 px-3 py-2">
                          {row.values[col.id] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        // Handle other field types
        if (field.type === 'checkbox') {
          return (
            <span key={index} className="font-medium">
              {value ? t('common.yes') : t('common.no')}
            </span>
          );
        }

        if (field.type === 'date' && value) {
          try {
            const date = new Date(value as string);
            return (
              <span key={index} className="font-medium">
                {date.toLocaleDateString(dateLocaleFor(lang), { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            );
          } catch {
            return <span key={index} className="font-medium">{String(value)}</span>;
          }
        }

        // Handle select fields - show Bulgarian label instead of raw value
        if (field.type === 'select' && value && field.options) {
          const option = field.options.find((o: { value: string; label: { bg?: string; de?: string; en?: string } | string }) => o.value === value);
          if (option) {
            const label = typeof option.label === 'string'
              ? option.label
              : localizedText(option.label, lang, String(value));
            return <span key={index} className="font-medium">{label}</span>;
          }
        }

        return <span key={index} className="font-medium">{String(value || '')}</span>;
      })}
    </>
  );
}

export default function AssignmentsPage() {
  const { t, i18n } = useTranslation();
  // `i18n.language` може да е с регион („de-AT"); ключовете на декларациите са
  // двубуквени. Без свиването резолверът не намира нищо и пада на резервата.
  const lang = ((i18n.language || 'bg').split('-')[0]) as Language;
  const { customClaims, userProfile } = useAuth();
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const [viewModal, setViewModal] = useState<ViewModalData | null>(null);
  const [loadingView, setLoadingView] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState('');
  const [revokeSuccess, setRevokeSuccess] = useState('');
  /**
   * Съобщенията на екрана — на мястото на четирите `alert()`.
   *
   * 🔴 `alert()` блокира страницата, не се превежда и не предлага изход: три от
   * четирите съобщаваха ГРЕШКА, без родителят да има какво да натисне. `retry`
   * носи точно липсващото действие.
   * ⚠️ Един стейт, не три: две едновременни съобщения на един екран се борят за
   * вниманието и родителят не знае кое е последното.
   */
  const [notice, setNotice] = useState<
    { kind: 'error' | 'success'; message: string; retry?: () => void } | null
  >(null);

  // Use custom hooks instead of manual fetch
  const {
    assignments,
    isSuccess: assignmentsSuccess,
    error: assignmentsError,
    noFamily,
    refreshFailed: assignmentsRefreshFailed,
    refetch: refetchAssignments
  } = useAssignments();

  const {
    children,
    isLoading: childrenLoading,
    error: childrenError,
    refreshFailed: childrenRefreshFailed,
    refetch: refetchChildren,
  } = useChildren();

  /**
   * 🔴 „Първото зареждане падна“ ≠ „обновяването падна“ (ревю кр.2, 20.10). Само първото сменя
   * страницата с панел; при второто данните от сървъра остават, а с тях и отворената форма за
   * подпис — иначе фоново обновяване (връщане от камерата) затваря формата и губи попълненото.
   * Децата са част от зареждането: без тях формата за подпис и филтърът по дете не работят.
   */
  const loadError = (assignmentsRefreshFailed ? null : assignmentsError)
    ?? (childrenRefreshFailed ? null : childrenError);
  const refreshError = (assignmentsRefreshFailed ? assignmentsError : null)
    ?? (childrenRefreshFailed ? childrenError : null);
  const retryAll = () => {
    refetchAssignments();
    refetchChildren();
  };

  /**
   * 🔴 Заглавието идва от ЖИВАТА декларация, не от снимката в възлагането.
   *
   * `assignment.declarationTitle` е копие, направено в момента на възлагането;
   * преименуване в админ панела не го обновява, тоест родителят продължава да
   * вижда старото име. Снимката НЕ се трие от базата — само спираме да я четем,
   * за да е обратимо.
   *
   * ⚠️ Цената е едно четене на колекция от шест документа; `useDeclarations` е
   * установеният начин и вече се ползва от админските екрани. Правилата пускат
   * родител: `allow read: if belongsToSchool(schoolId)` (`firestore.rules:85`),
   * и за `get`, и за `list` — сверено, защото Firestore е мокнат в тестовете и
   * отказ от правилата НЕ би излязъл червен при нас.
   */
  const { declarations } = useDeclarations();
  const declarationByKey = useMemo(() => {
    const map = new Map<string, Declaration>();
    for (const declaration of declarations) {
      // Възлагането сочи ту по id на документа, ту по поле `code` — и двата пътя
      // съществуват в базата, затова и двата ключа влизат в указателя.
      if (declaration.declarationId) map.set(declaration.declarationId, declaration);
      if (declaration.code) map.set(declaration.code, declaration);
    }
    return map;
  }, [declarations]);

  /** Заглавието на едно възлагане — един резолвер, викан от ВСИЧКИ четци тук. */
  const titleOf = (assignment: Assignment): string =>
    liveDeclarationTitle(declarationByKey.get(assignment.declarationCode), lang) ||
    assignment.declarationCode;

  // 🔴 „Зарежда" до УСПЕШНИ данни, не до `!isLoading`: паузирана заявка е празна без грешка, и
  // тогава страницата казваше „няма чакащи", а пряката връзка — „декларацията не е намерена" (39А §11.2).
  const loading = (!assignmentsSuccess && !assignmentsError) || childrenLoading;
  const error = loadError?.message || null;

  /**
   * `?assignmentId=` — пряка връзка към формата за подпис (кутията „Спешна карта").
   *
   * 🔴 Адресът НЕ се вярва: id-то се търси в ЗАРЕДЕНИЯ списък, който вече е ограничен до
   * семейството. Чуждо, несъществуващо или вече неподписуемо id дава видимо съобщение,
   * никога модал и никога тишина. След обработка параметърът се маха (`replace`), за да
   * не се отвори модалът пак при затваряне или презареждане.
   * ⚠️ Чака и децата: `SignModal` ги иска.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get('assignmentId');
  useEffect(() => {
    // Без семейство списъкът не е търсен — „не е намерена“ би било лъжа; панелът обяснява.
    if (!deepLinkId || loading || error || noFamily) return;
    const target = assignments.find((a) => a.assignmentId === deepLinkId);
    if (target?.status === 'pending') {
      setSelectedAssignment(target);
    } else if (!target) {
      setNotice({ kind: 'error', message: t('assignments.deepLink.notFound') });
    } else if (target.status === 'signed') {
      setNotice({ kind: 'success', message: t('assignments.deepLink.alreadySigned') });
    } else {
      setNotice({ kind: 'error', message: t('assignments.deepLink.notPending') });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('assignmentId');
    setSearchParams(next, { replace: true });
  }, [deepLinkId, loading, error, noFamily, assignments, searchParams, setSearchParams, t]);

  /**
   * `?childId=` — „чакащи за това дете“ от страниците „Деца“ и детето. До 19.10 параметърът
   * не се четеше: връзката водеше към целия списък. Филтър само за дете от СЕМЕЙСТВОТО —
   * чуждо или непознато id не филтрира (иначе списъкът би бил празен без причина).
   */
  const childFilter = children.find((c) => c.childId === searchParams.get('childId')) ?? null;
  const clearChildFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('childId');
    next.delete('status');
    setSearchParams(next, { replace: true });
  };
  const visibleAssignments = childFilter
    ? assignments.filter((a) => a.childId === childFilter.childId)
    : assignments;
  /** `&status=pending` от същите връзки — само чакащите на детето (без него — и подписаните). */
  const onlyPending = !!childFilter && searchParams.get('status') === 'pending';

  /**
   * След подпис се обезсилва ЦЕЛИЯТ префикс на възлаганията, не само списъка на тази
   * страница. Заявките са еднократни (`getDocs`) със `staleTime` 5 мин, а детската страница
   * чете ДРУГ ключ (`byChild`) — без това тя до 5 минути показва „подпиши" за вече
   * подписана декларация и повторната проверка на кутията изобщо не тръгва.
   */
  const queryClient = useQueryClient();
  const invalidateAllAssignments = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.school.assignments.all(customClaims?.schoolId || '') });

  const pendingAssignments = visibleAssignments.filter((a) => a.status === 'pending');
  const completedAssignments = onlyPending ? [] : visibleAssignments.filter((a) => a.status !== 'pending');

  const isConsentBased = (declCode: string) => {
    const basis = DEFAULT_LEGAL_BASIS[declCode];
    return basis ? CONSENT_BASED.has(basis) : false;
  };

  const handleRevokeConsent = async (assignment: Assignment) => {
    const confirmed = confirm(t('assignments.revokeConfirm'));
    if (!confirmed) return;
    setRevokingId(assignment.assignmentId);
    setRevokeError('');
    setRevokeSuccess('');
    try {
      const fn = httpsCallable(functions, 'revokeConsent');
      await fn({ assignmentId: assignment.assignmentId });
      setRevokeSuccess(t('assignments.revokeSuccess'));
      refetchAssignments();
      setTimeout(() => setRevokeSuccess(''), 5000);
    } catch (err: unknown) {
      setRevokeError(getErrorMessage(err) || t('assignments.revokeError'));
    } finally {
      setRevokingId(null);
    }
  };

  // Handle View Declaration
  async function handleViewDeclaration(assignment: Assignment) {
    if (!customClaims?.schoolId) return;

    setLoadingView(assignment.assignmentId);
    try {
      // Load declaration content and schema
      let declarationContent = '';
      let fieldsSchema: FieldConfig[] | undefined;
      const declarationCode = assignment.declarationCode;

      // Try by document ID first
      const declarationRef = doc(db, `schools/${customClaims.schoolId}/declarations/${declarationCode}`);
      let declarationSnap = await getDoc(declarationRef);

      if (!declarationSnap.exists()) {
        // Try by code field
        const declarationsRef = collection(db, `schools/${customClaims.schoolId}/declarations`);
        const declarationQuery = query(declarationsRef, where('code', '==', declarationCode), limit(1));
        const querySnapshot = await getDocs(declarationQuery);
        if (!querySnapshot.empty) {
          declarationSnap = querySnapshot.docs[0];
        }
      }

      let templateUrl: string | undefined;
      if (declarationSnap.exists()) {
        const data = declarationSnap.data();
        const rawContent =
          localizedText(data.content, lang) || t('assignments.contentUnavailable');
        // Replace system placeholders with actual values
        declarationContent = replaceSystemPlaceholders(
          rawContent,
          userProfile?.displayName || '',
          assignment.childName || '',
          lang
        );
        fieldsSchema = data.fieldsSchema?.fields;
        templateUrl = data.templateUrl || undefined;
      }

      // Load signature data - include parentUserId for Firestore rules
      let signatureData = null;
      const currentUser = auth.currentUser;
      if (currentUser) {
        const signaturesRef = collection(db, `schools/${customClaims.schoolId}/signatures`);
        // 🔴 БЕЗ `limit(1)`: върната за повторно попълване декларация има ПОВЕЧЕ
        // от един подпис — старият остава като одитна следа. Заявка без подредба
        // връща произволен от тях, тоест екранът може да покаже ОТКАЗА вместо
        // новия подпис. Подредбата е в паметта (виж shared/utils/signatureOrder),
        // за да не иска нов композитен индекс — липсващ индекс гърми на живо.
        const signatureQuery = query(
          signaturesRef,
          where('assignmentId', '==', assignment.assignmentId),
          where('parentUserId', '==', currentUser.uid)
        );
        const signatureSnapshot = await getDocs(signatureQuery);
        const newest = pickNewestSignature(
          signatureSnapshot.docs.map((d) => ({ ...d.data(), signatureId: d.id })),
        );
        if (newest) {
          signatureData = newest;
        }
      }

      setViewModal({
        assignment,
        declarationContent,
        fieldsSchema,
        signatureData,
        templateUrl,
      });
    } catch (error) {
      console.error('Error loading declaration:', error);
      setNotice({
        kind: 'error',
        message: t('assignments.loadError'),
        retry: () => handleViewDeclaration(assignment),
      });
    } finally {
      setLoadingView(null);
    }
  }

  // Handle PDF download
  async function handleDownloadPDF(assignmentId: string) {
    // For iOS Safari: open window immediately (in user gesture handler)
    // then navigate to PDF URL after fetch completes
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    let pdfWindow: Window | null = null;

    if (isIOS) {
      // Open a loading page immediately to avoid popup blocker
      pdfWindow = window.open('about:blank', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write(`
          <html>
            <head><title>${t('assignments.pdfWindowTitle')}</title></head>
            <body style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:system-ui;color:#666;">
              <div style="text-align:center;">
                <p style="font-size:18px;">${t('assignments.pdfWindowHeading')}</p>
                <p style="font-size:14px;">${t('assignments.pdfWindowWait')}</p>
              </div>
            </body>
          </html>
        `);
      }
    }

    try {
      setDownloadingPdf(assignmentId);

      // Get current user and ID token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const idToken = await user.getIdToken();

      // Call HTTP function
      const response = await appCheckedFetch(
        `https://europe-west3-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/generatePDF`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({ assignmentId }),
        }
      );

      const data = await response.json().catch(() => ({}));

      // TASK_52 — преведено съобщение по статуса, не суровият английски текст на сървъра.
      // 403 „нямате право" не се предлага за повторен опит — отговорът няма да се промени.
      if (!response.ok || !data.success) {
        if (pdfWindow) pdfWindow.close();
        setNotice({
          kind: 'error',
          message: t(pdfDownloadErrorKey(response.status)),
          ...(response.status === 403 ? {} : { retry: () => handleDownloadPDF(assignmentId) }),
        });
        return;
      }

      if (data.documentUrl) {
        if (pdfWindow) {
          // Navigate the pre-opened window to PDF
          pdfWindow.location.href = data.documentUrl;
        } else {
          // For non-iOS, try normal window.open, fallback to anchor download
          const newWindow = window.open(data.documentUrl, '_blank');
          if (!newWindow) {
            // Fallback: create anchor and trigger download
            const link = document.createElement('a');
            link.href = data.documentUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }
      } else {
        if (pdfWindow) pdfWindow.close();
        setNotice({
          kind: 'error',
          message: `${t('assignments.pdfGenerateError')}: ${data.message || t('assignments.unknownError')}`,
          retry: () => handleDownloadPDF(assignmentId),
        });
      }
    } catch (error: unknown) {
      console.error('Error downloading PDF:', error);
      if (pdfWindow) pdfWindow.close();
      setNotice({
        kind: 'error',
        message: `${t('assignments.pdfDownloadError')}: ${getErrorMessage(error) || t('assignments.unknownError')}`,
        retry: () => handleDownloadPDF(assignmentId),
      });
    } finally {
      setDownloadingPdf(null);
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  // Error state — със заглавие и „Опитай пак“; празен кеш без сървър се назовава (19.10, № 1116).
  if (loadError) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('assignments.title')} subtitle={t('assignments.subtitle')} />
        <AssignmentsLoadProblem error={loadError} onRetry={retryAll} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('assignments.title')}
        subtitle={t('assignments.subtitle')}
      />

      {notice?.kind === 'error' && (
        <InlineError
          message={notice.message}
          scrollIntoView
          onDismiss={() => setNotice(null)}
          dismissLabel={t('common.close')}
          action={
            notice.retry && (
              <button type="button" className="btn btn-secondary text-sm" onClick={notice.retry}>
                {t('assignments.retry')}
              </button>
            )
          }
        />
      )}
      {notice?.kind === 'success' && (
        <InlineStatus
          message={notice.message}
          onDismiss={() => setNotice(null)}
          dismissLabel={t('common.close')}
        />
      )}

      {/* 🔴 Никога празна страница: без семейство / нищо възложено — казано с думи (19.10, № 1116). */}
      {noFamily && <AssignmentsNoFamily />}
      {refreshError && <RefreshProblem error={refreshError} onRetry={retryAll} />}
      {childFilter && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
          <span>
            {t(onlyPending ? 'assignments.filter.childPending' : 'assignments.filter.child', { name: childFilter.childName })}
          </span>
          <button type="button" className="btn btn-secondary text-sm" onClick={clearChildFilter}>
            {t('assignments.filter.showAll')}
          </button>
        </div>
      )}
      {!noFamily && pendingAssignments.length + completedAssignments.length === 0 && (
        <div className="card text-gray-600">
          {!childFilter
            ? t('assignments.state.empty')
            : t(onlyPending ? 'assignments.state.emptyPendingForChild' : 'assignments.state.emptyForChild', { name: childFilter.childName })}
        </div>
      )}

      {/* Pending Assignments */}
      {pendingAssignments.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {t('assignments.pending')} ({pendingAssignments.length})
          </h2>
          <div className="space-y-3">
            {pendingAssignments.map((assignment) => {
              const dueDate = assignment.dueDate?.toDate?.();
              const dueDateStr = dueDate ? dueDate.toLocaleDateString(dateLocaleFor(lang)) : '';

              return (
                <div
                  key={assignment.assignmentId}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-yellow-200 bg-yellow-50 rounded-lg gap-3"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {titleOf(assignment)}
                    </h3>
                    <p className="text-sm text-gray-600">{assignment.childName}</p>
                    {dueDateStr && (
                      <p className="text-sm text-yellow-600 mt-1">
                        {t('assignments.dueDate')}: {dueDateStr}
                      </p>
                    )}
                  </div>
                  <Tooltip text={t('assignments.signTooltip')} position="left">
                    <button
                      className="btn btn-primary w-full sm:w-auto"
                      onClick={() => setSelectedAssignment(assignment)}
                    >
                      {t('assignments.signButton')}
                    </button>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completed Assignments */}
      {completedAssignments.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {t('assignments.completed')} ({completedAssignments.length})
          </h2>
          {revokeError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-800">{revokeError}</div>
          )}
          {revokeSuccess && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-800">{revokeSuccess}</div>
          )}
          <div className="space-y-3">
            {completedAssignments.map((assignment) => {
              const signedAt = assignment.metadata?.lastStatusChangeAt?.toDate?.();
              const signedAtStr = signedAt ? signedAt.toLocaleString(dateLocaleFor(lang), {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }) : '';

              return (
                <div
                  key={assignment.assignmentId}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg gap-3"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {titleOf(assignment)}
                    </h3>
                    <p className="text-sm text-gray-600">{assignment.childName}</p>
                    {signedAtStr && (
                      <p className={`text-sm mt-1 ${assignment.status === 'revoked' ? 'text-red-600' : assignment.status === 'declined' ? 'text-orange-600' : 'text-green-600'}`}>
                        {assignment.status === 'revoked'
                          ? `${t('assignments.statusRevoked')}: ${signedAtStr}`
                          : assignment.status === 'declined'
                          ? `${t('assignments.statusDeclined')}: ${signedAtStr}`
                          : `${t('assignments.statusSigned')}: ${signedAtStr}`}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Tooltip text={t('assignments.viewTooltip')} position="top">
                      <button
                        className="btn btn-secondary text-sm flex-1 sm:flex-initial"
                        onClick={() => handleViewDeclaration(assignment)}
                        disabled={loadingView === assignment.assignmentId}
                      >
                        {loadingView === assignment.assignmentId ? t('common.loading') : t('assignments.viewButton')}
                      </button>
                    </Tooltip>
                    <Tooltip text={t('assignments.downloadTooltip')} position="top">
                      <button
                        className="btn btn-secondary text-sm flex-1 sm:flex-initial"
                        onClick={() => handleDownloadPDF(assignment.assignmentId)}
                        disabled={downloadingPdf === assignment.assignmentId}
                      >
                        {downloadingPdf === assignment.assignmentId
                          ? t('assignments.generating')
                          : t('assignments.downloadPDF')}
                      </button>
                    </Tooltip>
                    {assignment.status === 'signed' && isConsentBased(assignment.declarationCode) && (
                      <Tooltip text={t('assignments.revokeTooltip')} position="top">
                        <button
                          className="btn btn-danger text-sm flex-1 sm:flex-initial"
                          onClick={() => handleRevokeConsent(assignment)}
                          disabled={revokingId === assignment.assignmentId}
                        >
                          {revokingId === assignment.assignmentId
                            ? t('assignments.revoking')
                            : t('assignments.revokeButton')}
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sign Modal */}
      {selectedAssignment && (
        <SignModal
          assignment={selectedAssignment}
          children={children}
          onClose={() => setSelectedAssignment(null)}
          onSuccess={() => {
            setSelectedAssignment(null);
            // Обезсилването презарежда и активния списък на тази страница — отделен
            // `refetchAssignments()` би го прекъснал и пуснал втори път.
            void invalidateAllAssignments();
            // Ключът съществуваше в трите локала, но никой не го викаше —
            // немският и английският му никога не са били на екран.
            setNotice({ kind: 'success', message: t('signModal.success') });
          }}
        />
      )}

      {/* View Modal */}
      {viewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {titleOf(viewModal.assignment)}
                </h2>
                <Tooltip text={t('common.close')} position="left">
                  <button
                    onClick={() => setViewModal(null)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </Tooltip>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">{t('assignments.childLabel')}</p>
                  <p className="font-medium">{viewModal.assignment.childName}</p>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-2">{t('assignments.declarationTextTitle')}</h3>
                  <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap text-sm">
                    <DeclarationContentRenderer
                      content={viewModal.declarationContent}
                      fieldsSchema={viewModal.fieldsSchema}
                      fieldValues={viewModal.signatureData?.fieldValues?.values}
                    />
                  </div>
                  {viewModal.templateUrl && (
                    <div className="mt-3">
                      <a
                        href={viewModal.templateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        {t('assignments.viewFullDocument')}
                      </a>
                    </div>
                  )}
                </div>

                {viewModal.signatureData && (
                  <div className="border-t pt-4">
                    <h3 className="font-medium text-gray-900 mb-2">{t('assignments.signatureInfoTitle')}</h3>
                    <div className="bg-green-50 p-4 rounded-lg space-y-2">
                      <p className="text-sm">
                        <span className="text-gray-600">{t('assignments.signedByLabel')}</span>{' '}
                        <span className="font-medium">{viewModal.signatureData.parentName}</span>
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-600">{t('assignments.dateLabel')}</span>{' '}
                        <span className="font-medium">
                          {viewModal.signatureData.responseAt?.toDate?.()?.toLocaleString(dateLocaleFor(lang)) || 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-600">{t('assignments.statusLabel')}</span>{' '}
                        {/*
                          🔴 Цветът идваше от закован `text-green-600` — тоест ОТКАЗАНА
                          декларация се изписваше в ЗЕЛЕНО. Точно формата, срещу която
                          съществува споделената таблица: отказът да не изглежда като успех.
                          Вариантът се чете от `DECLARATION_STATUS_DISPLAY`, а текстът от
                          `declarations.status.*` — и двете вече съществуват.
                          ⚠️ `ResponseType` е „signed | declined" (сверено в `shared/types`),
                          тоест двоичният избор ТУК е верен: това е отговорът на един подпис,
                          не състоянието на възлагането, което има четири стойности.
                        */}
                        <Badge variant={declarationStatusDisplay(viewModal.signatureData.responseType)?.variant ?? 'neutral'}>
                          {t(`declarations.status.${viewModal.signatureData.responseType}`)}
                        </Badge>
                      </p>
                    </div>
                  </div>
                )}

                {/* Pickup Photos — only for signed PICKUP_AUTH declarations */}
                {viewModal.assignment.declarationCode === 'PICKUP_AUTH' &&
                  viewModal.assignment.signatureId &&
                  viewModal.signatureData?.pickupAuth?.authorizedPersons && (
                    <PickupPhotosSection
                      signatureId={viewModal.assignment.signatureId}
                      authorizedPersons={viewModal.signatureData.pickupAuth.authorizedPersons}
                    />
                  )}

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Tooltip text={t('assignments.downloadTooltip')} position="top">
                    <button
                      onClick={() => {
                        handleDownloadPDF(viewModal.assignment.assignmentId);
                        setViewModal(null);
                      }}
                      className="btn btn-primary"
                    >
                      {t('assignments.downloadPDF')}
                    </button>
                  </Tooltip>
                  <Tooltip text={t('assignments.closeTooltip')} position="top">
                    <button
                      onClick={() => setViewModal(null)}
                      className="btn btn-secondary"
                    >
                      {t('common.close')}
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
